import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import LoadingContainer from '@/shared/components/loading/LoadingContainer';
import SimplePanel from '@/shared/components/SimplePanel';
import { CanCreate, showWhenPermission } from '@/shared/components/security';
import { features, functionalities, privileges } from '@/shared/services/AuthService';
import showWhenFeature from '@/shared/components/security/showWhenFeature';
import { useGetIntraCompanyOverview } from './hooks/useGetIntraCompanyOverview';
import IntraCompanyTable from './IntraCompanyTable';
import IntraCompanyFilters from './IntraCompanyFilters';
import messages from './messages';
import { initialFilters } from './helpers';
import useBillCompany from './hooks/useBillCompany';
import useBatchToggleIntraCompanyBilling from './hooks/useBatchToggleIntraCompanyBilling';
import Button from '@/shared/components/input/select/Button';
import useBatchBill from './hooks/useBatchBill';
import { useRowSelection } from '@/shared/hooks/useRowSelection';
const storageKey = 'IntraCompanyFilters';
const IntraCompanyBillingContainer = ({ stateService, }) => {
    const intl = useIntl();
    const [currentFilters, setFilters] = useState(() => initialFilters(sessionStorage.getItem(storageKey), undefined));
    const [details, setDetails] = useState({});
    const { data, isLoading, reload, setIsProcessing } = useGetIntraCompanyOverview(currentFilters);
    const { billCompany } = useBillCompany();
    const { batchBill, batchBillRunning } = useBatchBill();
    const { toggleIntraCompanyBilling } = useBatchToggleIntraCompanyBilling();
    const { isSelected, resetSelected, toggleSelected, hasAnySelected, selectAll, } = useRowSelection();
    useEffect(() => {
        sessionStorage.setItem(storageKey, JSON.stringify(currentFilters));
        setDetails({});
    }, [currentFilters]);
    const onBillCompany = useCallback(async ({ relationCompanyId, userCompanyId, key }) => {
        const result = await billCompany({
            relationCompanyId,
            userCompanyId,
            filters: currentFilters,
        });
        setIsProcessing([key]);
        if (result) {
            reload();
            setDetails({});
        }
    }, [billCompany, currentFilters, reload]);
    const onBatchBill = useCallback(async () => {
        const selectedLines = data
            .filter(x => isSelected(x.key))
            .map(({ relationCompanyId, userCompanyId }) => ({
            relationCompanyId,
            userCompanyId,
        }));
        setIsProcessing(data.filter(x => isSelected(x.key)).map(x => x.key));
        await batchBill({
            selectedLines,
            filters: currentFilters,
        });
    }, [batchBill, currentFilters, data, isSelected]);
    const onToggleIntraCompanyBilling = useCallback(async ({ relationCompanyId, userCompanyId }) => {
        await toggleIntraCompanyBilling({
            relationCompanyId,
            userCompanyId,
            neverInvoice: !currentFilters.neverIntraCompany,
            filters: currentFilters,
        });
        reload();
        setDetails({});
    }, [currentFilters, toggleIntraCompanyBilling, reload]);
    const updateFilters = (filters) => {
        setFilters((oldFilters) => {
            const newFilters = Object.assign(Object.assign({}, oldFilters), filters);
            if (JSON.stringify(oldFilters) === JSON.stringify(newFilters))
                return oldFilters;
            return newFilters;
        });
    };
    const toggleDetails = (key) => setDetails(d => {
        return Object.assign(Object.assign({}, d), { [key]: !d[key] });
    });
    const onSelectAll = (selected) => {
        if (selected) {
            selectAll(data.filter(x => !!x.relation && !x.isProcessing).map(x => x.key));
        }
        else {
            resetSelected();
        }
    };
    const isGlobalActionsDisabled = batchBillRunning || !hasAnySelected;
    return (_jsxs("div", { children: [_jsx(SimplePanel, Object.assign({ article: "12187295654684", title: intl.formatMessage(messages.IntraCompanyBilling) }, { children: _jsx("div", Object.assign({ className: "info-block container-fluid radius4B" }, { children: _jsx(IntraCompanyFilters, { filters: currentFilters, onFilterChange: updateFilters, intl: intl }) })) })), _jsxs(LoadingContainer, Object.assign({ isLoading: isLoading || !data }, { children: [data && data.length > 0 && (_jsx("div", Object.assign({ className: "row btn-row" }, { children: _jsx("div", Object.assign({ className: "col-md-6 pull-right content-right" }, { children: _jsx(CanCreate, Object.assign({ on: "IntraCompanyInvoice" }, { children: _jsx(Button, Object.assign({ disabled: isGlobalActionsDisabled, onClick: onBatchBill, className: "" }, { children: _jsx(FormattedMessage, Object.assign({}, messages.MakeInvoiceProposals)) })) })) })) }))), _jsx(IntraCompanyTable, { data: data, details: details, filters: currentFilters, onToggleDetails: toggleDetails, onBillCompany: onBillCompany, onToggleIntraCompanyBilling: onToggleIntraCompanyBilling, neverIntraCompany: currentFilters.neverIntraCompany, onToggleSelected: toggleSelected, isSelected: isSelected, onToggleAllSelected: onSelectAll })] }))] }));
};
export default showWhenPermission(functionalities.IntraCompanyInvoice, privileges.Read)(showWhenFeature(features.IntraCompanyBilling)(IntraCompanyBillingContainer));
